import {
  useAddUserToShippingLocationGroupsMutation,
  useEditOrgUserLocationGroupsMutation,
  useToggleM5SyncForUserMutation,
  useUnsubscribeFromShippingLocationGroupsMutation,
} from '@/buyers/_gen/gql'
import LocationGroupSelector from '@/buyers/components/LocationGroupSelector'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Action from '@/gf/components/Action'
import Select from '@/gf/components/next/Select'
import Spinner from '@/gf/components/Spinner'
import { TableV2, Tbody, Td, Th, Thead, Tr } from '@/gf/components/Table'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import { ApolloError } from '@apollo/client'
import { Pencil, Trash2 } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import type { LocationGroup, LocationGroupUser } from './index'
import DeleteLocationGroupModal from './DeleteLocationGroupModal'
import EditEmployeeIdModal from './EditEmployeeIdModal'
import EditLocationGroupModal from './EditLocationGroupModal'

type GroupedUsers = {
  groupId: string | null
  groupName: string | null
  users: LocationGroupUser[]
}

const LocationGroupsTable = ({
  users,
  locationGroups,
  refetch,
}: {
  users: LocationGroupUser[]
  locationGroups: LocationGroup[]
  refetch: () => void
}) => {
  const client = useGqlClient()
  const [_, msgr] = useMsgs()
  const { featureFlags } = useSession()
  const [editOrgUser] = useEditOrgUserLocationGroupsMutation({ client })
  const [toggleM5Sync] = useToggleM5SyncForUserMutation({ client })
  const [addUserToLocationGroups] = useAddUserToShippingLocationGroupsMutation({ client })
  const [unsubscribeFromLocationGroups] = useUnsubscribeFromShippingLocationGroupsMutation({
    client,
  })
  const [selectedGroup, setSelectedGroup] = useState<LocationGroup | null>(null)
  const [selectedUser, setSelectedUser] = useState<LocationGroupUser | null>(null)
  const [showEditGroupModal, editGroupModalActions] = useToggle()
  const [showDeleteGroupModal, deleteGroupModalActions] = useToggle()
  const [showEditEmployeeIdModal, editEmployeeIdModalActions] = useToggle()

  // Loading states for async operations
  const [syncLoadingUsers, setSyncLoadingUsers] = useState<Set<string>>(new Set())
  const [subscribersLoadingGroups, setSubscribersLoadingGroups] = useState<Set<string>>(new Set())
  const [locationGroupLoadingUsers, setLocationGroupLoadingUsers] = useState<Set<string>>(new Set())

  // Optimistic state for subscription users to keep UI in sync
  const [optimisticSubscribers, setOptimisticSubscribers] = useState<Map<string, string[]>>(
    new Map()
  )

  // Sync optimistic state with actual data when locationGroups changes
  useEffect(() => {
    const newMap = new Map<string, string[]>()
    locationGroups.forEach((group) => {
      newMap.set(group.id, group.subscriptionUsers?.map((u) => u.id) || [])
    })
    setOptimisticSubscribers(newMap)
  }, [locationGroups])

  // Define role order for sorting
  const roleOrder: Record<string, number> = {
    requester: 1,
    purchaser: 2,
    admin: 3,
  }

  // Group users by location groups
  const groupedUsers: GroupedUsers[] = (() => {
    // Users without location groups
    const usersWithoutGroups = users.filter((u) => !u.shippingLocationGroups?.length)

    // Users grouped by location group
    const groupMap = new Map<string, LocationGroupUser[]>()

    users.forEach((user) => {
      user.shippingLocationGroups?.forEach((group) => {
        if (!group) return
        if (!groupMap.has(group.id)) {
          groupMap.set(group.id, [])
        }
        groupMap.get(group.id)!.push(user)
      })
    })

    const sortUsers = (userList: LocationGroupUser[]) =>
      [...userList].sort((a, b) => {
        const aRole = a.userRole?.name?.toLowerCase() || 'user'
        const bRole = b.userRole?.name?.toLowerCase() || 'user'
        const aOrder = roleOrder[aRole] || 99
        const bOrder = roleOrder[bRole] || 99
        if (aOrder !== bOrder) return aOrder - bOrder
        return a.displayName.localeCompare(b.displayName)
      })

    const result: GroupedUsers[] = []

    // Add users without groups first
    if (usersWithoutGroups.length > 0) {
      result.push({
        groupId: null,
        groupName: null,
        users: sortUsers(usersWithoutGroups),
      })
    }

    // Add all groups (including empty ones) and sort alphabetically
    const groupsWithUsers = locationGroups
      .map((group) => ({
        groupId: group.id,
        groupName: group.name,
        users: sortUsers(groupMap.get(group.id) || []),
      }))
      .sort((a, b) => a.groupName.localeCompare(b.groupName))

    result.push(...groupsWithUsers)

    return result
  })()

  const updateUserLocationGroups = (userId: string, groupIds: string[]) => {
    // Add user to loading set
    setLocationGroupLoadingUsers((prev) => new Set(prev).add(userId))

    editOrgUser({
      variables: {
        userId,
        shippingLocationGroupIds: groupIds,
        externalId: null,
        name: null,
        phoneNumber: null,
        title: null,
      },
    })
      .then(() => refetch())
      .catch((err: ApolloError) => {
        const message = err.graphQLErrors?.map((error) => error.message).join('; ') || err.message
        msgr.add(message || 'Failed to update location group', 'negative')
      })
      .finally(() => {
        // Remove user from loading set after refetch completes
        setLocationGroupLoadingUsers((prev) => {
          const next = new Set(prev)
          next.delete(userId)
          return next
        })
      })
  }

  const toggleSyncForUser = (userId: string, enabled: boolean) => {
    // Add user to loading set
    setSyncLoadingUsers((prev) => new Set(prev).add(userId))

    toggleM5Sync({
      variables: {
        userId,
        enabled,
      },
    })
      .then(() => refetch())
      .catch((err: ApolloError) => {
        const message = err.graphQLErrors?.map((error) => error.message).join('; ') || err.message
        msgr.add(message || 'Failed to update sync setting', 'negative')
      })
      .finally(() => {
        // Remove user from loading set after refetch completes
        setSyncLoadingUsers((prev) => {
          const next = new Set(prev)
          next.delete(userId)
          return next
        })
      })
  }

  const updateSubscribers = (
    locationGroupId: string,
    newSubscriberIds: string[],
    currentSubscriberIds: string[]
  ) => {
    // Mark this group as loading
    setSubscribersLoadingGroups((prev) => new Set(prev).add(locationGroupId))

    const added = newSubscriberIds.filter((id) => !currentSubscriberIds.includes(id))
    const removed = currentSubscriberIds.filter((id) => !newSubscriberIds.includes(id))

    const mutations: Promise<unknown>[] = []

    if (added.length > 0) {
      added.forEach((userId) =>
        mutations.push(
          addUserToLocationGroups({
            variables: { userId, locationGroupIds: [locationGroupId] },
          })
        )
      )
    }

    if (removed.length > 0) {
      removed.forEach((userId) =>
        mutations.push(
          unsubscribeFromLocationGroups({
            variables: { userId, locationGroupIds: [locationGroupId] },
          })
        )
      )
    }

    Promise.all(mutations)
      .then(() => refetch())
      .catch((err: ApolloError) => {
        const message = err.graphQLErrors?.map((error) => error.message).join('; ') || err.message
        msgr.add(message || 'Failed to update subscribers', 'negative')
        return refetch()
      })
      .finally(() => {
        // Remove from loading set after refetch completes
        setSubscribersLoadingGroups((prev) => {
          const next = new Set(prev)
          next.delete(locationGroupId)
          return next
        })
      })
  }

  return (
    <>
      <div className="overflow-y-auto max-h-[calc(100vh-20rem)] relative">
        <TableV2 className="min-w-full">
          <Thead className="static after:hidden">
            <Tr className="bg-white">
              <Th>User</Th>
              <Th>Role</Th>
              <Th>Employee ID</Th>
              <Th>Location Group</Th>
            </Tr>
          </Thead>
          <Tbody>
            {groupedUsers.map((group) => (
              <React.Fragment key={group.groupId || 'no-group'}>
                {/* Group Header */}
                <Tr className="bg-gray-50 sticky -top-px z-30">
                  <Td colSpan={4} className="font-semibold text-gray-700 bg-gray-50 relative">
                    <div className="h-px bg-gray-300 w-full absolute bottom-0 inset-x-0" />
                    <div className="flex items-center gap-x-3">
                      <span>
                        {group.groupName || 'No Location Group'} ({group.users.length})
                      </span>
                      {group.groupId && (
                        <>
                          <Action.S
                            className="-my-0.5 p-1.5 rounded-full"
                            onClick={() => {
                              const locationGroup = locationGroups.find(
                                (lg) => lg.id === group.groupId
                              )
                              if (locationGroup) {
                                setSelectedGroup(locationGroup)
                                editGroupModalActions.on()
                              }
                            }}
                          >
                            <Pencil className="w-4 h-4 text-gray-700" />
                          </Action.S>
                          <Action.S
                            className="-my-0.5 p-1.5 rounded-full"
                            onClick={() => {
                              const locationGroup = locationGroups.find(
                                (lg) => lg.id === group.groupId
                              )
                              if (locationGroup) {
                                setSelectedGroup(locationGroup)
                                deleteGroupModalActions.on()
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-gray-700" />
                          </Action.S>
                        </>
                      )}
                    </div>
                  </Td>
                </Tr>

                {/* Subscribers */}
                {group.groupName && (
                  <Tr>
                    <Td className="bg-gray-50" colSpan={4}>
                      <div className="flex items-center gap-x-2">
                        <span className="text-sm text-gray-600">Subscribers:</span>
                        <div className="min-w-[250px] font-normal relative">
                          <Select
                            isMulti
                            isDisabled={
                              group.groupId ? subscribersLoadingGroups.has(group.groupId) : false
                            }
                            options={users.map((user) => ({
                              value: user.id,
                              label: user.displayName,
                            }))}
                            value={(() => {
                              if (!group.groupId) return []
                              const subscriberIds = optimisticSubscribers.get(group.groupId) || []
                              return subscriberIds
                                .map((userId) => {
                                  const user = users.find((u) => u.id === userId)
                                  return user ? { value: user.id, label: user.displayName } : null
                                })
                                .filter(
                                  (opt): opt is { value: string; label: string } => opt !== null
                                )
                            })()}
                            onChange={(newValue: readonly { value: string; label: string }[]) => {
                              if (!group.groupId) return

                              const currentSubscriberIds =
                                optimisticSubscribers.get(group.groupId) || []
                              const newSubscriberIds = newValue.map((opt) => opt.value)

                              // Update optimistic state immediately for instant UI feedback
                              setOptimisticSubscribers((prev) => {
                                const next = new Map(prev)
                                next.set(group.groupId!, newSubscriberIds)
                                return next
                              })
                              updateSubscribers(
                                group.groupId,
                                newSubscriberIds,
                                currentSubscriberIds
                              )
                            }}
                            placeholder="Select subscribers..."
                            isClearable
                          />
                          {group.groupId && subscribersLoadingGroups.has(group.groupId) && (
                            <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none">
                              <Spinner size="xs" color="blue" className="mx-0" />
                            </div>
                          )}
                        </div>
                      </div>
                    </Td>
                  </Tr>
                )}

                {/* Users in this group */}
                {group.users.map((user) => {
                  return (
                    <Tr key={user.id}>
                      <Td>
                        <div>
                          <div className="font-medium text-gray-900">{user.displayName}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </Td>
                      <Td>
                        <span className="text-sm text-gray-900 capitalize">
                          {user.userRole?.name || 'User'}
                        </span>
                      </Td>
                      <Td>
                        <div className="space-y-2">
                          <div className="gap-x-2 flex items-center">
                            {user.externalId ? (
                              <>
                                {user.externalId}
                                <Action.S
                                  className="-my-0.5 p-1 rounded-full"
                                  onClick={() => {
                                    setSelectedUser(user)
                                    editEmployeeIdModalActions.on()
                                  }}
                                >
                                  <Pencil className="w-4 h-4 text-gray-700" />
                                </Action.S>
                              </>
                            ) : (
                              <Action.T
                                className="italic text-gray-500"
                                onClick={() => {
                                  setSelectedUser(user)
                                  editEmployeeIdModalActions.on()
                                }}
                              >
                                Add Employee ID
                              </Action.T>
                            )}
                          </div>
                          {user.externalId &&
                            (featureFlags.assetworksApiIssuePartsToWorkOrder ?? false) && (
                              <label className="flex items-center text-sm text-gray-700 gap-x-1.5">
                                <input
                                  type="checkbox"
                                  checked={user.m5SyncEnabled ?? false}
                                  onChange={(e) => toggleSyncForUser(user.id, e.target.checked)}
                                  disabled={syncLoadingUsers.has(user.id)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                                />
                                {syncLoadingUsers.has(user.id) && (
                                  <Spinner size="xs" color="blue" className="mx-0" />
                                )}
                                Sync Requests
                              </label>
                            )}
                        </div>
                      </Td>
                      <Td>
                        <div className="max-w-72">
                          <LocationGroupSelector
                            value={
                              user.shippingLocationGroups
                                ?.filter((g) => g != null)
                                .map((g) => g.id) ?? []
                            }
                            onChange={(groupIds) => updateUserLocationGroups(user.id, groupIds)}
                            isLoading={locationGroupLoadingUsers.has(user.id)}
                          />
                        </div>
                      </Td>
                    </Tr>
                  )
                })}

                {/* Empty */}
                {group.users.length === 0 && (
                  <Tr>
                    <Td colSpan={4} className="text-gray-500 italic">
                      No users
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            ))}

            {groupedUsers.length === 0 && (
              <Tr>
                <Td colSpan={4} className="text-center text-gray-500">
                  No users found
                </Td>
              </Tr>
            )}
          </Tbody>
        </TableV2>
      </div>

      {showEditGroupModal && selectedGroup && (
        <EditLocationGroupModal
          locationGroup={selectedGroup}
          onClose={() => {
            editGroupModalActions.off()
            setSelectedGroup(null)
          }}
          onSuccess={() => {
            refetch()
            editGroupModalActions.off()
            setSelectedGroup(null)
          }}
        />
      )}

      {showDeleteGroupModal && selectedGroup && (
        <DeleteLocationGroupModal
          locationGroup={selectedGroup}
          onClose={() => {
            deleteGroupModalActions.off()
            setSelectedGroup(null)
          }}
          onSuccess={() => {
            refetch()
            deleteGroupModalActions.off()
            setSelectedGroup(null)
          }}
        />
      )}

      {showEditEmployeeIdModal && selectedUser && (
        <EditEmployeeIdModal
          user={selectedUser}
          onClose={() => {
            editEmployeeIdModalActions.off()
            setSelectedUser(null)
          }}
          onSuccess={() => {
            refetch()
            editEmployeeIdModalActions.off()
            setSelectedUser(null)
          }}
        />
      )}
    </>
  )
}

export default LocationGroupsTable
